import { useCallback, useMemo } from 'react';

import {
  type PermissionsMachineImplementations,
  useInitPermissionsMachine,
  usePermissionsMachine,
} from '@sg/shared-fe/features/permissions';

import { useTelemetry } from '@order/Telemetry';

import { useLocationPermissionPrimerEnabled } from '../useLocationPermissionPrimerEnabled';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Drives the location permission primer flow for a specific entry point.
 * `variants` is the FF allow-list this consumer responds to.
 *
 * Returns the flags + handlers the consumer needs to decide whether to render
 * a loading screen, the primer, or its normal content.
 */
export const useLocationPermissionPrimer = (params: Params) => {
  const { variants } = params;

  const { track } = useTelemetry();
  const { canEnableLocationPrimer, locationPrimerVariant } = useLocationPermissionPrimerEnabled();

  // ─────────────────────────────────────────────────────────────────────

  const isLocationPrimerEnabled =
    canEnableLocationPrimer &&
    (locationPrimerVariant === 'disabled' ? false : variants.includes(locationPrimerVariant));

  // ─── Telemetry ───────────────────────────────────────────────────────

  const entryPoint = VARIANT_ENTRY_POINT[locationPrimerVariant];

  const telemetryPayload = useMemo(
    () => ({ entry_point: entryPoint, variant: locationPrimerVariant }),
    [entryPoint, locationPrimerVariant],
  );

  const onLocationPrimerShow = useCallback(() => {
    track('location_primer_shown', telemetryPayload);
  }, [track, telemetryPayload]);

  const onRequestLocationPermission = useCallback(() => {
    track('location_primer_cta_tapped', telemetryPayload);
  }, [track, telemetryPayload]);

  const onSkipLocationPermission = useCallback(() => {
    track('location_primer_dismissed', telemetryPayload);
  }, [track, telemetryPayload]);

  const implementations = useMemo<PermissionsMachineImplementations>(
    () => ({
      actions: { onLocationPrimerShow, onRequestLocationPermission, onSkipLocationPermission },
    }),
    [onLocationPrimerShow, onRequestLocationPermission, onSkipLocationPermission],
  );

  // ─── Actor ───────────────────────────────────────────────────────────

  const permissionsActorRef = useInitPermissionsMachine({
    isLocationPrimerEnabled,
    implementations,
  });

  const {
    isLocationPermissionsLoading,
    isLocationPrimerVisible,
    isRequestingLocationPermission,
    isSkippingLocationPermission,
    isLocationPermissionDenied,
    isLocationPermissionSkipped,
    isLocationPermissionAlreadyShown,
    requestLocationPermissions,
    skipLocationPermissions,
  } = usePermissionsMachine(permissionsActorRef);

  // ─── Derived ─────────────────────────────────────────────────────────

  const isPrimerActive =
    isLocationPrimerVisible || isRequestingLocationPermission || isSkippingLocationPermission;

  // ─────────────────────────────────────────────────────────────────────

  return {
    isLocationPrimerEnabled,
    isLocationPermissionsLoading,
    isPrimerActive,
    isRequestingLocationPermission,
    isSkippingLocationPermission,
    isLocationPermissionDenied,
    isLocationPermissionSkipped,
    isLocationPermissionAlreadyShown,
    onAllow: requestLocationPermissions,
    onSkip: skipLocationPermissions,
  };
};

// ─── Constants ───────────────────────────────────────────────────────────────

const VARIANT_ENTRY_POINT = {
  disabled: undefined,
  A: 'onboarding',
  B: 'location-search',
  C: 'order-confirmation',
} as const;

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  variants: Exclude<
    ReturnType<typeof useLocationPermissionPrimerEnabled>['locationPrimerVariant'],
    'disabled'
  >[];
};
